import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { X, Send, AlertTriangle } from "lucide-react";

/**
 * Masse-DM-modal for admin/owner.
 *
 * Props:
 *   open: bool                — om modalen er synlig
 *   onClose: () => void       — lukker
 *   filter: object            — søke-filter som sendes til /admin/broadcast/preview & send
 *                                (samme felt som BroadcastFilter på backend)
 *
 * Flyt:
 *   1. Bruker åpner modal — vi henter preview (count + sample) automatisk
 *   2. Bruker skriver melding
 *   3. Bruker klikker SEND → bekreftelses-trinn → faktisk send
 */
export default function AdminBroadcastModal({ open, onClose, filter }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [body, setBody] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingPreview(true);
    setPreview(null);
    setConfirming(false);
    api.post("/admin/broadcast/preview", filter || {})
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Kunne ikke hente preview."))
      .finally(() => setLoadingPreview(false));
  }, [open, filter]);

  if (!open) return null;

  const eligible = preview?.eligible ?? 0;
  const tooMany  = !!preview?.exceeds_limit;
  const canSend  = !sending && !tooMany && body.trim().length >= 2 && eligible > 0;

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/broadcast/send", {
        filter: filter || {},
        body: body.trim(),
      });
      toast.success(`Sendt til ${r.data.sent} av ${r.data.eligible} mottakere.`);
      onClose();
      setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke sende.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-4" data-testid="broadcast-modal">
      <div className="bg-[var(--noir-elev)] border border-white/15 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-meta text-[var(--magenta)] text-xs uppercase tracking-[0.18em] mb-1">
              ADMIN · MASSE-DM
            </div>
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
              Send DM til søkeresultatet
            </h2>
          </div>
          <button onClick={onClose} className="text-[var(--cream-faint)] hover:text-[var(--cream)]" data-testid="broadcast-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="border border-white/10 p-4 mb-5 bg-[var(--noir)]" data-testid="broadcast-preview-stats">
          {loadingPreview ? (
            <div className="text-[var(--cream-soft)] text-sm">Henter mottakerantall …</div>
          ) : preview ? (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="font-meta text-xs text-[var(--cream-soft)] uppercase">Matchet</span>
                <span className="font-fraunces text-2xl text-[var(--cream)]">{preview.matched}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="font-meta text-xs text-[var(--cream-soft)] uppercase">Får faktisk DM</span>
                <span className="font-fraunces text-2xl text-[var(--magenta)]" data-testid="broadcast-eligible-count">{preview.eligible}</span>
              </div>
              {preview.skipped_dm_blocked > 0 && (
                <div className="flex items-baseline justify-between text-xs text-[var(--cream-faint)]">
                  <span>Hoppet over (har reservert seg / blokkert)</span>
                  <span>{preview.skipped_dm_blocked}</span>
                </div>
              )}
              {preview.sample?.length > 0 && (
                <div className="pt-2 border-t border-white/10 mt-3">
                  <div className="font-meta text-[10px] text-[var(--cream-faint)] uppercase mb-1">Eksempler:</div>
                  <div className="text-xs text-[var(--cream-soft)]">
                    {preview.sample.map((s) => s.username).join(", ")}
                    {preview.matched > preview.sample.length && ` … (+${preview.matched - preview.sample.length} flere)`}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {tooMany && (
          <div className="border-l-4 border-amber-500 bg-amber-500/10 p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-[var(--cream)]">
              Du har over {preview?.max_per_send} mottakere. Innstram søk-kriteriene og prøv igjen.
            </div>
          </div>
        )}

        {/* Tekst */}
        <label className="font-meta text-xs text-[var(--cream-soft)] uppercase block mb-2">Melding</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
          rows={6}
          className="input-editorial w-full"
          placeholder="Skriv meldingen som skal sendes til alle mottakerne …"
          data-testid="broadcast-message-body"
        />
        <div className="text-xs text-[var(--cream-faint)] mt-1 text-right">{body.length}/4000</div>

        {/* Knapper */}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            className="btn-ghost"
            disabled={sending}
            data-testid="broadcast-cancel"
          >
            Avbryt
          </button>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={!canSend}
              className="btn-magenta inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="broadcast-prepare-send"
            >
              <Send className="h-4 w-4" /> Send til {eligible} brukere
            </button>
          ) : (
            <button
              onClick={doSend}
              disabled={sending}
              className="btn-magenta inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="broadcast-confirm-send"
            >
              {sending ? "Sender …" : `Bekreft og send (${eligible})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
